import { Button, Switch } from '@c4ads/c4blocks';
import { zodResolver } from '@hookform/resolvers/zod';
import {
    Dialog,
    DialogContent,
    DialogTitle,
    FormControlLabel,
    FormGroup,
    FormHelperText,
    FormLabel,
    TextField,
    Typography,
    styled,
} from '@mui/material';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import { useOutletContext } from 'react-router';
import { z } from 'zod';

import { useAuth } from '../../api/auth';
import { AccountOutletContextInterface } from '../../routes/Account';

const StyledForm = styled('form')(({ theme }) => ({
    display: 'flex',
    flexDirection: 'column',
    width: 360,
    gap: theme.spacing(2),
    padding: theme.spacing(4),
    margin: '0 auto',
}));

const schema = z.object({
    email: z.string().email(),
    first_name: z.string().min(2, {
        message: 'First name must be at least 2 characters.',
    }),
    last_name: z.string().min(2, {
        message: 'Last name must be at least 2 characters.',
    }),
    view_courtcase: z.boolean(),
    view_defendant: z.boolean(),
    view_seizure: z.boolean(),
    view_seizedcommodity: z.boolean(),
});

export default function UserInviteForm({ open, setOpen }: { open: boolean; setOpen: (value: boolean) => void }) {
    const { user } = useOutletContext<AccountOutletContextInterface>();

    const { signup } = useAuth();
    const {
        control,
        formState: { errors },
        reset,
        handleSubmit,
    } = useForm<z.infer<typeof schema>>({
        resolver: zodResolver(schema),
        defaultValues: {
            email: undefined,
            first_name: undefined,
            last_name: undefined,
            view_courtcase: true,
            view_defendant: true,
            view_seizure: true,
            view_seizedcommodity: true,
        },
    });

    const handleClose = () => {
        setOpen(false);
    };

    const onSubmit: SubmitHandler<any> = ({ ...userData }) => {
        if (user?.isAdmin || user?.isSuperuser) {
            const { email, first_name, last_name, ...permissions } = userData;
            const organization_id = user?.organization.id;
            const permission_codenames: string[] = [];

            Object.entries(permissions).forEach(([k, v]) => {
                v && permission_codenames.push(k);
            });

            const data = { email, first_name, last_name, organization_id, permission_codenames };

            signup(data);
            reset();
            handleClose();
        }
    };

    return (
        <Dialog open={open} onClose={handleClose} fullWidth maxWidth="xs">
            <DialogTitle>Invite User</DialogTitle>
            <DialogContent>
                <StyledForm onSubmit={handleSubmit(onSubmit)}>
                    <Controller
                        name="email"
                        control={control}
                        rules={{
                            required: 'User email is required.',
                        }}
                        render={({ field }) => <TextField label="Email" {...field} />}
                    />
                    {errors.email && <FormHelperText color="error">{errors.email?.message as string}</FormHelperText>}
                    <Controller
                        name="first_name"
                        control={control}
                        rules={{
                            required: 'First name is required.',
                        }}
                        render={({ field }) => <TextField label="First Name" {...field} />}
                    />
                    {errors.first_name && (
                        <FormHelperText color="error">{errors.first_name?.message as string}</FormHelperText>
                    )}
                    <Controller
                        name="last_name"
                        control={control}
                        rules={{
                            required: 'Last name is required.',
                        }}
                        render={({ field }) => <TextField label="Last Name" {...field} />}
                    />
                    {errors.last_name && (
                        <FormHelperText color="error">{errors.last_name?.message as string}</FormHelperText>
                    )}
                    <FormGroup>
                        <FormLabel>Permissions</FormLabel>
                        <Controller
                            name="view_courtcase"
                            control={control}
                            render={({ field }) => (
                                <FormControlLabel
                                    control={<Switch {...field} defaultChecked />}
                                    label={
                                        <Typography variant="overline" color="primary">
                                            View Court Cases
                                        </Typography>
                                    }
                                />
                            )}
                        />
                        <Controller
                            name="view_defendant"
                            control={control}
                            render={({ field }) => (
                                <FormControlLabel
                                    control={<Switch {...field} defaultChecked />}
                                    label={
                                        <Typography variant="overline" color="primary">
                                            View Defendants
                                        </Typography>
                                    }
                                />
                            )}
                        />
                        <Controller
                            name="view_seizure"
                            control={control}
                            render={({ field }) => (
                                <FormControlLabel
                                    control={<Switch {...field} defaultChecked />}
                                    label={
                                        <Typography variant="overline" color="primary">
                                            View Seizures
                                        </Typography>
                                    }
                                />
                            )}
                        />
                        <Controller
                            name="view_seizedcommodity"
                            control={control}
                            render={({ field }) => (
                                <FormControlLabel
                                    control={<Switch {...field} defaultChecked />}
                                    label={
                                        <Typography variant="overline" color="primary">
                                            View Seized Commodities
                                        </Typography>
                                    }
                                />
                            )}
                        />
                    </FormGroup>
                    <Button type="submit" variant="contained">
                        Send Invite
                    </Button>
                </StyledForm>
            </DialogContent>
        </Dialog>
    );
}
